import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Dialog, DialogActions, DialogContent, Tab, Tabs } from '@mui/material';
import Typo from '../Typo';
import { breakpoints, media } from '../../utils/media';
import { useTranslation } from 'react-i18next';
import SimpleButton from '../molecules/Button/SimpleButton';
import { toRgba } from '../theme/colors';
import theme from '../theme';

const DialogWrapper = styled(Dialog)`
  &.MuiModal-root.MuiDialog-root {
    z-index: 9999;
  }

  .MuiBackdrop-root {
    background-color: ${toRgba(theme.colors.NEU_999, 0.76)};
  }

  .MuiDialog-paper {
    overflow: ${(props) => props.overflow ?? 'auto'};
    padding: ${({ compact, noBottomMargin }) => (compact === 'true' ? '0' : noBottomMargin === true ? '20px 20px 0' : '20px')};
    padding-top: ${(props) => props.fullScreen && '60px'};
    min-width: 400px;
    min-height: 300px;
    border-radius: 24px;

    ${media.lessThan('phablet')} {
      min-width: 0 !important;
      max-width: 100% !important;
      border-radius: 0;
    }
  }

  .MuiDialogContent-root {
    padding: ${({ compact }) => (compact === 'true' ? '0' : '20px 24px')};
    ${media.lessThan('phablet')} {
      padding: 0;
    }
  }
`;

const ModalTitle = styled.div`
  padding: 0 24px;
  padding-bottom: ${({ $titleHasLine }) => ($titleHasLine ? '1rem' : '0')};
  border-bottom: ${(props) => (props.$titleHasLine ? '1px' : '0')} solid ${(props) => props.theme.colors.NEU_200};

  ${media.lessThan('phablet')} {
    padding: 0;
    padding-bottom: ${({ $titleHasLine }) => ($titleHasLine ? '1rem' : '0')};
  }
`;

const ModalActions = styled.div`
  padding-top: 16px;
  align-items: center;
  display: flex;
  justify-content: flex-end;
  button:not(:first-of-type) {
    margin-left: 8px;
  }

  ${media.lessThan('tablet')} {
    padding: 1rem;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
`;

const TabWrapper = styled.div`
  margin: 1rem 0;
  ${media.lessThan('phablet')} {
    margin: 0;
  }
  .MuiTabs-indicator {
    background-color: ${(props) => props.theme.colors.NEU_PRIMARY} !important;
  }
`;
const TabStyled = styled(Tab)`
  font-family: ${(props) => props.theme.fonts.HEADING_FONT}, sans-serif !important;
  font-weight: 600 !important;
  font-size: ${(props) => props.theme.fontSizes.medium} !important;
  &.Mui-selected {
    color: ${(props) => props.theme.colors.NEU_PRIMARY} !important;
  }
`;
const TitleWrapper = styled.div`
  margin-top: 1rem;
`;

const Modal = ({
  open,
  onClose,
  showActions,
  onAccept,
  acceptLabel,
  acceptIsDisabled,
  acceptIsLoading,
  title,
  titleHasLine,
  titleChildren,
  children,
  width,
  compact,
  compactOnMobile,
  handleTabChange,
  navigationItems,
  selectedTab,
  variant,
  dialogActionsChildren,
  minWidth,
  noBottomMargin,
  id,
  maxWidth,
  ...props
}) => {
  const showTitle = title || titleChildren;
  const fullScreen = window.innerWidth <= breakpoints.phablet;

  const { t } = useTranslation();

  const isCompact = useMemo(() => {
    if (compact || (compactOnMobile && fullScreen)) return 'true';
  }, [fullScreen, compactOnMobile, compact]);

  return (
    <DialogWrapper
      open={!!open}
      compact={isCompact}
      onClose={onClose}
      scroll={'paper'}
      fullScreen={fullScreen}
      noBottomMargin={noBottomMargin}
      maxWidth={maxWidth || 'md'}
      sx={{ '& .MuiDialog-paper': { width: width, minWidth: minWidth, maxWidth: maxWidth } }}
      {...props}
    >
      {onClose && (
        <CloseButtonWrapper>
          <SimpleButton variant='ghost' size='small' icon='Close' aria-label={t('globals.close')} onClick={onClose} />
        </CloseButtonWrapper>
      )}

      {showTitle && (
        <ModalTitle $titleHasLine={titleHasLine} id='scroll-dialog-title'>
          {title ? (
            <TitleWrapper>
              <Typo.ContentSmallHeader $textAlign='left'>{title}</Typo.ContentSmallHeader>
            </TitleWrapper>
          ) : (
            titleChildren
          )}
          {!!navigationItems?.length && (
            <TabWrapper>
              <Tabs onChange={handleTabChange} variant='scrollable' scrollButtons={'auto'} allowScrollButtonsMobile value={selectedTab}>
                {navigationItems.map((item, index) => {
                  return <TabStyled key={index} label={item.label} aria-label={item.value} />;
                })}
              </Tabs>
            </TabWrapper>
          )}
        </ModalTitle>
      )}
      <DialogContent>{children}</DialogContent>
      {showActions && (
        <ModalActions>
          {!!onClose && (
            <SimpleButton variant='ghost' onClick={onClose}>
              {t('globals.cancel')}
            </SimpleButton>
          )}
          {!!onAccept && (
            <SimpleButton
              variant='primary'
              onClick={onAccept}
              disabled={acceptIsDisabled || acceptIsLoading}
              isLoading={acceptIsLoading}
              startIcon={!acceptIsLoading ? 'Save' : undefined}
            >
              {acceptLabel || t('globals.save')}
            </SimpleButton>
          )}
        </ModalActions>
      )}
      {dialogActionsChildren && <DialogActions>{dialogActionsChildren}</DialogActions>}
    </DialogWrapper>
  );
};

export default Modal;
